import { SystemStyleObject } from '@chakra-ui/react';

export const glassyEffectButton: SystemStyleObject = {
  position: 'relative',
  background: 'rgba(96, 119, 156, 0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '8px',
  '@supports not (backdrop-filter: blur(10px))': {
    background: 'rgba(96, 119, 156, 0.15)',
  },

  _before: {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    border: '1px solid transparent',
    background:
      'linear-gradient(145deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.15) 100%) border-box',
    WebkitMask:
      'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    '@supports not (mask-composite: exclude) and not (-webkit-mask-composite: destination-out)':
      {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
      },
  },
};

/** Glassy effect for modal/container (e.g. disclaimer modal), same as button without hover */
export const glassyEffectModal: SystemStyleObject = {
  position: 'relative',
  background: 'rgba(96, 119, 156, 0.08)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  transform: 'translate3d(0, 0, 0)',
  '@supports ((hanging-punctuation: first) and (font: -apple-system-body)) or (-webkit-touch-callout: none)':
    {
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      background: 'gray.800',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.32)',
    },

  _before: {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    border: '1px solid transparent',
    background:
      'linear-gradient(145deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.15) 100%) border-box',
    WebkitMask:
      'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    '@supports ((hanging-punctuation: first) and (font: -apple-system-body)) or (-webkit-touch-callout: none)':
      {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
      },
  },
};

export const glassyEffectButtonHover: SystemStyleObject = {
  _hover: {
    background: 'rgba(137, 82, 224, 0.2)',
    boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  },

  _active: {
    background: 'rgba(137, 82, 224, 0.2)',
    boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  },
};

export const getGlassyEffectContainer = ({
  bg,
  border,
  bgHover,
  borderHover,
}: {
  bg: string;
  border: string;
  bgHover: string;
  borderHover: string;
}): SystemStyleObject => ({
  position: 'relative',
  background: bg,
  backdropFilter: 'blur(50px)',
  WebkitBackdropFilter: 'blur(50px)',
  boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  '@supports not (backdrop-filter: blur(50px))': {
    background: bgHover,
  },
  transition: 'background 0.3s, box-shadow 0.3s',
  transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',

  _before: {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    border: `1px solid`,
    borderColor: border,
    background:
      'linear-gradient(145deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.01) 100%) border-box',
    WebkitMask:
      'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    '@supports not (mask-composite: exclude) and not (-webkit-mask-composite: destination-out)':
      {
        background: 'transparent',
        border: `1px solid`,
        borderColor: borderHover,
      },
  },

  _hover: {
    background: bgHover,
    boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  },
});

export const glassyEffectAuthLayout: SystemStyleObject = {
  position: 'relative',
  background: 'rgba(23, 26, 29, 0.25)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '8px',
  '@supports not (backdrop-filter: blur(10px))': {
    background: 'rgba(255, 255, 255, 0.15)',
  },

  _before: {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    border: '1px solid transparent',
    background:
      'linear-gradient(145deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 30%, rgba(255, 255, 255, 0.03) 48%, rgba(255, 255, 255, 0.01) 64%, transparent 82%) border-box',
    WebkitMask:
      'linear-gradient(to bottom, #fff 0%, #fff 40%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,0.75) 56%, rgba(255,255,255,0.45) 64%, rgba(255,255,255,0.2) 71%, rgba(255,255,255,0.08) 77%, rgba(255,255,255,0.02) 82%, transparent 87%) padding-box, linear-gradient(to bottom, #fff 0%, #fff 40%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,0.75) 56%, rgba(255,255,255,0.45) 64%, rgba(255,255,255,0.2) 71%, rgba(255,255,255,0.08) 77%, rgba(255,255,255,0.02) 82%, transparent 87%)',
    mask: 'linear-gradient(to bottom, #fff 0%, #fff 40%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,0.75) 56%, rgba(255,255,255,0.45) 64%, rgba(255,255,255,0.2) 71%, rgba(255,255,255,0.08) 77%, rgba(255,255,255,0.02) 82%, transparent 87%) padding-box, linear-gradient(to bottom, #fff 0%, #fff 40%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,0.75) 56%, rgba(255,255,255,0.45) 64%, rgba(255,255,255,0.2) 71%, rgba(255,255,255,0.08) 77%, rgba(255,255,255,0.02) 82%, transparent 87%)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    '@supports not (mask-composite: exclude) and not (-webkit-mask-composite: destination-out)':
      {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
  },

  _hover: {
    background: 'rgba(255, 255, 255, 0.08)',
    boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  },

  _active: {
    background: 'rgba(255, 255, 255, 0.12)',
    boxShadow: 'inset 0 0 32px rgba(0, 0, 0, .3)',
  },

  _after: {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '-8px',
    width: '100vw',
    height: '30%',
    borderRadius: 'inherit',
    borderBottomLeftRadius: 'inherit',
    borderBottomRightRadius: 'inherit',
    background:
      'linear-gradient(to top, #171A1D 0%, rgba(23, 26, 29, 0.9) 40%, rgba(23, 26, 29, 0.6) 70%, transparent 100%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
};

export const glassyEffectPrimary: SystemStyleObject = {
  position: 'relative',
  background: 'rgba(96, 119, 156, 0.08)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  '@supports (hanging-punctuation: first) and (font: -apple-system-body)': {
    backdropFilter: 'none !important',
    WebkitBackdropFilter: 'none !important',
    background: 'rgba(96, 119, 156, 0.2)',
  },

  _before: {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    border: '1px solid transparent',
    background:
      'linear-gradient(145deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.15) 100%) border-box',
    WebkitMask:
      'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    '@supports not (mask-composite: exclude) and not (-webkit-mask-composite: destination-out)':
      {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
      },
  },
};
